/**
 * Client HTTP unique de la SPA.
 *
 * Il concentre trois responsabilites que l'on ne veut pas voir eparpillees
 * dans les composants : l'ajout du jeton, la serialisation des query params
 * (les tableaux sont repetes : `departement=75&departement=92`, format attendu
 * par les schemas Zod de `@leads/shared`) et la traduction du format d'erreur
 * de l'API en exception exploitable.
 */
import type { ErreurApiDTO } from '@leads/shared';

const CLE_JETON = 'leads.jeton';

/** Base configurable pour un deploiement ou l'API n'est pas sur la meme origine. */
const BASE_API: string = (import.meta.env.VITE_API_URL as string | undefined) ?? '';

/* -------------------------------------------------------------------------- */
/*                                   Erreurs                                   */
/* -------------------------------------------------------------------------- */

export class ErreurApi extends Error {
  readonly statut: number;
  readonly code: string;
  readonly details: unknown;

  constructor(statut: number, code: string, message: string, details?: unknown) {
    super(message);
    this.name = 'ErreurApi';
    this.statut = statut;
    this.code = code;
    this.details = details;
  }

  get estSessionExpiree(): boolean {
    return this.statut === 401;
  }
}

const MESSAGES_STATUT: Record<number, string> = {
  400: 'Requête invalide.',
  401: 'Session expirée, veuillez vous reconnecter.',
  403: "Vous n'avez pas les droits nécessaires pour cette action.",
  404: 'Ressource introuvable.',
  409: 'Conflit : la ressource existe déjà ou a été modifiée entre-temps.',
  422: 'Les données envoyées ne sont pas valides.',
  429: 'Trop de requêtes, veuillez patienter quelques instants.',
  500: "Erreur interne du serveur.",
  502: "L'API est momentanément injoignable.",
  503: "Le service est momentanément indisponible.",
};

/**
 * Message d'un refus de permission (403 NON_AUTORISE). Celui de l'API nomme
 * la permission technique (« Permission requise : leads.importer ») : on le
 * remplace par une phrase.
 */
export const MESSAGE_ACCES_REFUSE =
  'Votre rôle ne permet pas cette action. Si vous en avez besoin, demandez-le à un administrateur.';

/** Message lisible pour l'utilisateur, quelle que soit l'origine de l'erreur. */
export function messageErreur(erreur: unknown): string {
  if (erreur instanceof ErreurApi) return erreur.message;
  if (erreur instanceof DOMException && erreur.name === 'AbortError') {
    return 'Requête annulée.';
  }
  if (erreur instanceof TypeError) {
    return "Impossible de joindre l'API. Vérifiez votre connexion réseau.";
  }
  if (erreur instanceof Error && erreur.message) return erreur.message;
  return "Une erreur inattendue est survenue.";
}

/**
 * Message d'un 404 recu en ecrivant sur un lead (interaction, suivi...) : la
 * fiche a pu sortir du perimetre de l'appelant entre l'ouverture de l'ecran et
 * l'envoi (prise par un collegue, supprimee). Sinon, message habituel.
 */
export function messageEcritureLead(erreur: unknown): string {
  if (erreur instanceof ErreurApi && erreur.statut === 404) {
    return "Ce lead n'est plus accessible : un collègue vient peut-être de le prendre.";
  }
  return messageErreur(erreur);
}

/**
 * Message d'un 404 recu en ecrivant sur un element d'un lead (« Cet echange »,
 * « Ce contact ») : deja supprime, ou lead sorti du perimetre, l'API ne les
 * distingue pas (meme code). Message neutre ; la fiche relue montre le reste.
 */
export function messageEcritureElementLead(erreur: unknown, element: string): string {
  if (erreur instanceof ErreurApi && erreur.statut === 404) return `${element} n'existe plus, ou ce lead n'est plus accessible.`;
  return messageErreur(erreur);
}

/** Details de validation renvoyes par Zod, aplatis pour l'affichage. */
export function detailsErreur(erreur: unknown): string[] {
  if (!(erreur instanceof ErreurApi) || !erreur.details) return [];
  const details = erreur.details;
  if (Array.isArray(details)) {
    return details.map((entree) => {
      if (typeof entree === 'string') return entree;
      if (entree && typeof entree === 'object') {
        const objet = entree as { path?: unknown; message?: unknown };
        const chemin = Array.isArray(objet.path) ? objet.path.join('.') : '';
        const message = typeof objet.message === 'string' ? objet.message : JSON.stringify(entree);
        return chemin ? `${chemin} : ${message}` : message;
      }
      return String(entree);
    });
  }
  if (typeof details === 'string') return [details];
  return [];
}

export function aCodeErreur(erreur: unknown, code: string): boolean {
  return erreur instanceof ErreurApi && erreur.code === code;
}

interface DetailChamp {
  champ: string;
  message: string;
}

function estDetailChamp(valeur: unknown): valeur is DetailChamp {
  if (typeof valeur !== 'object' || valeur === null) return false;
  const { champ, message } = valeur as Record<string, unknown>;
  return typeof champ === 'string' && typeof message === 'string';
}

/**
 * Message d'erreur d'un champ, tel que l'API le rend dans une 400 VALIDATION
 * (`details: [{ champ, message }]`) ; `undefined` pour toute autre erreur.
 */
export function erreurDeChamp(erreur: unknown, champ: string): string | undefined {
  if (!(erreur instanceof ErreurApi) || erreur.code !== 'VALIDATION') return undefined;
  const details: unknown[] = Array.isArray(erreur.details) ? erreur.details : [];
  return details.find((detail): detail is DetailChamp => estDetailChamp(detail) && detail.champ === champ)?.message;
}

/* -------------------------------------------------------------------------- */
/*                            Stockage du jeton JWT                            */
/* -------------------------------------------------------------------------- */

export function lireJeton(): string | null {
  try {
    return window.localStorage.getItem(CLE_JETON);
  } catch {
    // Navigation privee ou stockage bloque : on fonctionne sans persistance.
    return null;
  }
}

export function ecrireJeton(jeton: string | null): void {
  try {
    if (jeton) window.localStorage.setItem(CLE_JETON, jeton);
    else window.localStorage.removeItem(CLE_JETON);
  } catch {
    /* stockage indisponible : ignore volontairement */
  }
}

/**
 * Jeton change par un autre onglet (connexion sous un autre compte,
 * deconnexion) : `localStorage` est partage, et l'evenement `storage` ne
 * parvient qu'aux autres onglets. L'ecouteur recoit le jeton desormais memorise.
 */
export function surJetonModifieAilleurs(ecouteur: (jeton: string | null) => void): () => void {
  const surStockage = (evenement: StorageEvent): void => {
    // `key` nulle : tout le stockage vient d'etre vide.
    if (evenement.key !== null && evenement.key !== CLE_JETON) return;
    ecouteur(lireJeton());
  };
  window.addEventListener('storage', surStockage);
  return () => window.removeEventListener('storage', surStockage);
}

type Ecouteur = () => void;

interface Canal {
  abonner(ecouteur: Ecouteur): () => void;
  notifier(): void;
}

/** Le client HTTP previent, les fournisseurs React reagissent : ce module ignore routeur et toasts. */
function creerCanal(): Canal {
  const ecouteurs = new Set<Ecouteur>();
  return {
    abonner(ecouteur) {
      ecouteurs.add(ecouteur);
      return () => {
        ecouteurs.delete(ecouteur);
      };
    },
    notifier() {
      for (const ecouteur of ecouteurs) ecouteur();
    },
  };
}

const canalSessionExpiree = creerCanal();
const canalMotDePasseAChanger = creerCanal();
const canalAccesRefuse = creerCanal();

/**
 * Abonne un ecouteur aux 401 renvoyes par l'API : le contexte d'authentification
 * s'en sert pour purger la session et renvoyer vers la page de connexion.
 */
export const surSessionExpiree = canalSessionExpiree.abonner;

/** 403 CHANGEMENT_MOT_DE_PASSE_REQUIS : un mot de passe provisoire doit etre change avant tout. */
export const surChangementMotDePasseRequis = canalMotDePasseAChanger.abonner;

/** 403 NON_AUTORISE : une action refusee que l'interface aurait du masquer. */
export const surAccesRefuse = canalAccesRefuse.abonner;

/* -------------------------------------------------------------------------- */
/*                              Query parameters                               */
/* -------------------------------------------------------------------------- */

export type ValeurParam =
  | string
  | number
  | boolean
  | null
  | undefined
  | ReadonlyArray<string | number>;

export type ParamsRequete = Record<string, ValeurParam>;

/**
 * Serialise des parametres en query string.
 * Les valeurs vides sont omises et les tableaux repetent la cle.
 */
export function construireQuery(params: ParamsRequete | undefined): string {
  if (!params) return '';
  const recherche = new URLSearchParams();
  for (const [cle, valeur] of Object.entries(params)) {
    if (valeur === undefined || valeur === null || valeur === '') continue;
    if (Array.isArray(valeur)) {
      for (const element of valeur as ReadonlyArray<string | number>) {
        if (element === undefined || element === null || element === '') continue;
        recherche.append(cle, String(element));
      }
      continue;
    }
    recherche.append(cle, String(valeur));
  }
  return recherche.toString();
}

export function construireUrl(chemin: string, params?: ParamsRequete): string {
  const query = construireQuery(params);
  return `${BASE_API}${chemin}${query ? `?${query}` : ''}`;
}

/* -------------------------------------------------------------------------- */
/*                                  Appel API                                  */
/* -------------------------------------------------------------------------- */

export interface OptionsAppel {
  methode?: 'GET' | 'POST' | 'PATCH' | 'PUT' | 'DELETE';
  corps?: unknown;
  params?: ParamsRequete;
  signal?: AbortSignal;
  /**
   * Empeche l'emission de l'evenement de session expiree.
   * Utile sur la page de connexion ou un 401 signifie simplement
   * "identifiants incorrects" et non "jeton perime".
   */
  sansDeconnexion?: boolean;
}

function entetes(options: OptionsAppel, jeton: string | null): HeadersInit {
  const valeurs: Record<string, string> = { Accept: 'application/json' };
  if (options.corps !== undefined) valeurs['Content-Type'] = 'application/json';
  if (jeton) valeurs['Authorization'] = `Bearer ${jeton}`;
  return valeurs;
}

/**
 * `jetonEnvoye` : celui de la requete. Une reponse arrivee apres un
 * changement de session (mot de passe change, nouvelle connexion) parle de
 * l'ancien jeton : elle ne doit ni fermer ni reorienter la session en cours.
 */
async function construireErreur(
  reponse: Response,
  options: OptionsAppel,
  jetonEnvoye: string | null,
): Promise<ErreurApi> {
  let code = `HTTP_${reponse.status}`;
  let message = MESSAGES_STATUT[reponse.status] ?? `Erreur ${reponse.status}`;
  let details: unknown;

  try {
    const charge: unknown = await reponse.json();
    const enveloppe = charge as Partial<ErreurApiDTO> | null;
    if (enveloppe && typeof enveloppe === 'object' && enveloppe.erreur) {
      if (enveloppe.erreur.code) code = enveloppe.erreur.code;
      if (enveloppe.erreur.message) message = enveloppe.erreur.message;
      details = enveloppe.erreur.details;
    }
  } catch {
    // Corps absent ou non JSON : on conserve le message par defaut du statut.
  }

  const sessionEnCours = lireJeton() === jetonEnvoye;
  if (sessionEnCours && reponse.status === 401 && !options.sansDeconnexion) canalSessionExpiree.notifier();
  if (sessionEnCours && reponse.status === 403 && code === 'CHANGEMENT_MOT_DE_PASSE_REQUIS') {
    canalMotDePasseAChanger.notifier();
  }
  if (reponse.status === 403 && code === 'NON_AUTORISE') {
    message = MESSAGE_ACCES_REFUSE;
    canalAccesRefuse.notifier();
  }
  return new ErreurApi(reponse.status, code, message, details);
}

export async function appelApi<T>(chemin: string, options: OptionsAppel = {}): Promise<T> {
  const jeton = lireJeton();
  const reponse = await fetch(construireUrl(chemin, options.params), {
    method: options.methode ?? 'GET',
    headers: entetes(options, jeton),
    body: options.corps === undefined ? undefined : JSON.stringify(options.corps),
    signal: options.signal,
  });

  if (!reponse.ok) throw await construireErreur(reponse, options, jeton);

  if (reponse.status === 204) return undefined as T;
  const texte = await reponse.text();
  if (!texte) return undefined as T;
  return JSON.parse(texte) as T;
}

/* -------------------------------------------------------------------------- */
/*                            Telechargement fichier                           */
/* -------------------------------------------------------------------------- */

/**
 * Extrait le nom de fichier d'un entete `Content-Disposition`.
 * Gere la forme etendue `filename*=UTF-8''nom%20encode.csv` en priorite,
 * car c'est la seule qui supporte les accents.
 */
export function nomDepuisContentDisposition(entete: string | null | undefined): string | null {
  if (!entete) return null;
  const etendu = /filename\*\s*=\s*[^']*''([^;]+)/i.exec(entete);
  const brutEtendu = etendu?.[1]?.trim();
  if (brutEtendu) {
    try {
      return decodeURIComponent(brutEtendu);
    } catch {
      return brutEtendu;
    }
  }
  const guillemets = /filename\s*=\s*"([^"]*)"/i.exec(entete);
  if (guillemets?.[1]) return guillemets[1].trim();
  const simple = /filename\s*=\s*([^;]+)/i.exec(entete);
  return simple?.[1]?.trim() ?? null;
}

function declencherTelechargement(blob: Blob, nomFichier: string): void {
  const url = URL.createObjectURL(blob);
  const lien = document.createElement('a');
  lien.href = url;
  lien.download = nomFichier;
  lien.rel = 'noopener';
  document.body.appendChild(lien);
  lien.click();
  lien.remove();
  // Laisse au navigateur le temps de demarrer le telechargement.
  window.setTimeout(() => URL.revokeObjectURL(url), 2000);
}

/**
 * Telecharge un fichier genere par l'API (export CSV / XLSX / emailing).
 * Le jeton etant transmis par entete, on ne peut pas se contenter d'un lien.
 */
export async function telechargerFichier(
  chemin: string,
  params: ParamsRequete,
  nomParDefaut: string,
): Promise<string> {
  const jeton = lireJeton();
  const reponse = await fetch(construireUrl(chemin, params), {
    headers: jeton ? { Authorization: `Bearer ${jeton}` } : {},
  });
  if (!reponse.ok) throw await construireErreur(reponse, {}, jeton);

  const blob = await reponse.blob();
  const nom = nomDepuisContentDisposition(reponse.headers.get('Content-Disposition')) ?? nomParDefaut;
  declencherTelechargement(blob, nom);
  return nom;
}
